import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../api";

export const ProfileLogo = ({ profile, className }) => {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let obj;
    if (profile.logo_filename) {
      axios
        .get(`${API}/profiles/${profile.id}/logo`, { responseType: "blob" })
        .then((r) => {
          obj = URL.createObjectURL(r.data);
          setUrl(obj);
        })
        .catch(() => {});
    } else {
      setUrl(null);
    }
    return () => {
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [profile.id, profile.logo_filename]);

  const sizeClass = className || "w-9 h-9";

  if (url) {
    return <img src={url} alt={`${profile.name} logo`} className={`${sizeClass} rounded object-contain border bg-white flex-shrink-0`} />;
  }
  if (profile.profile_type === "Individual") {
    return (
      <div
        className={`${sizeClass} rounded bg-slate-200 flex items-center justify-center text-slate-500 flex-shrink-0`}
        aria-label={`${profile.name} avatar`}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-3/5 h-3/5" aria-hidden="true">
          <path d="M12 12c2.7 0 4.9-2.2 4.9-4.9S14.7 2.2 12 2.2 7.1 4.4 7.1 7.1 9.3 12 12 12zm0 2.4c-3.3 0-9.8 1.7-9.8 4.9v2.5h19.6v-2.5c0-3.2-6.5-4.9-9.8-4.9z" />
        </svg>
      </div>
    );
  }
  return (
    <div className={`${sizeClass} rounded bg-slate-200 flex items-center justify-center text-sm font-semibold text-slate-600 flex-shrink-0`}>
      {(profile.name || "?").charAt(0).toUpperCase()}
    </div>
  );
};

const ProfilesRail = ({ profiles, selectedProfileId, onSelect, onAddProfile, onEditProfile, collapsed = false, onToggleCollapsed }) => {
  if (collapsed) {
    return (
      <div className="w-9 flex-shrink-0" data-testid="profiles-rail">
        <div className="bg-white rounded-lg shadow h-full flex flex-col items-center py-3">
          <button
            onClick={onToggleCollapsed}
            data-testid="profiles-rail-toggle"
            title="Show Profiles"
            className="text-gray-500 hover:text-gray-800 mb-3"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <div className="flex flex-col gap-2 items-center overflow-y-auto px-1">
            {profiles.map((p) => (
              <button
                key={p.id}
                onClick={() => onSelect(p)}
                title={p.name}
                data-testid={`profile-item-collapsed-${p.id}`}
                className={`p-0.5 rounded ${selectedProfileId === p.id ? "ring-2 ring-blue-400" : ""}`}
              >
                <ProfileLogo profile={p} className="w-7 h-7" />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-56 flex-shrink-0" data-testid="profiles-rail">
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 bg-[#e1e2e4] rounded-t-lg flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <button
              onClick={onToggleCollapsed}
              data-testid="profiles-rail-toggle"
              title="Collapse Profiles"
              className="text-gray-500 hover:text-gray-800"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h2 className="text-sm font-semibold text-gray-900">Profiles</h2>
          </div>
          <button
            onClick={onAddProfile}
            data-testid="add-profile-button"
            className="text-blue-600 hover:text-blue-800 text-xs font-medium"
          >
            + Add
          </button>
        </div>
        <div className="p-2 space-y-1">
          {profiles.map((p) => (
            <div
              key={p.id}
              onClick={() => onSelect(p)}
              data-testid={`profile-item-${p.id}`}
              title={p.description || undefined}
              className={`group flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors border ${
                selectedProfileId === p.id
                  ? "bg-blue-50 border-blue-200"
                  : "border-transparent hover:bg-gray-50"
              }`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 break-words">{p.name}</p>
                <p className="text-xs text-gray-500">
                  {p.profile_type} · {p.account_count} {p.account_count === 1 ? "account" : "accounts"}
                </p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditProfile(p);
                }}
                data-testid={`edit-profile-${p.id}`}
                className="hidden group-hover:inline-block text-gray-400 hover:text-gray-700 text-xs flex-shrink-0 self-start"
              >
                Edit
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProfilesRail;
